import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { userActions } from '../../core/action/userAction';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import dayjs from 'dayjs';
import Select from 'react-select';
import {
  ChevronUp,
  PlusCircle,
  RotateCcw,
  Eye,
  Edit,
  Trash2,
  Search,
  Users,
  Phone,
} from 'feather-icons-react/build/IconComponents';
import { customerActions } from '../../core/action/customerActions';
import AddCustomer from '../../core/modals/peoples/AddCustomer';
import EditCustomer from '../../core/modals/peoples/EditCustomer';
import ViewCustomer from '../../core/modals/peoples/ViewCustomer';

const CustomerList = () => {
  const dispatch = useDispatch();
  const MySwal = withReactContent(Swal);
  const data = useSelector((state) => state.toggle_header);

  // Get user role for super admin check
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role_slug === 'super-admin';

  // Permission checks
  const canCreate = userActions.hasPermission('create-customers');
  const canEdit = userActions.hasPermission('edit-customers');
  const canDelete = userActions.hasPermission('delete-customers');
  const canView = userActions.hasPermission('view-customers');

  // State
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'name',
    sortOrder: 'ASC'
  });

  // Sort options
  const sortOptions = [
    { value: 'name_asc', label: 'Name (A-Z)' },
    { value: 'name_desc', label: 'Name (Z-A)' },
    { value: 'email_asc', label: 'Email (A-Z)' },
    { value: 'email_desc', label: 'Email (Z-A)' },
    { value: 'created_desc', label: 'Newest First' },
    { value: 'created_asc', label: 'Oldest First' }
  ];

  const [sortOption, setSortOption] = useState('name_asc');

  // Load customers
  const loadCustomers = useCallback(async (page = pagination.page) => {
    // Check if user has view permission
    if (!canView && !isSuperAdmin) {
      console.warn('User does not have permission to view customers');
      return;
    }

    try {
      setLoading(true);

      const [sortBy, sortOrder] = sortOption.split('_');

      const params = {
        page,
        limit: pagination.limit,
        search: filters.search,
        sort_by: sortBy === 'created' ? 'created_at' : sortBy,
        sort_order: sortOrder === 'desc' ? 'DESC' : 'ASC'
      };

      const result = await customerActions.getCustomers(params);

      if (result.success) {
        setCustomers(result.data || []);
        setPagination({
          page: result.page || page,
          limit: pagination.limit,
          total: result.total || 0,
          totalPages: result.totalPages || 1
        });
      }
    } catch (error) {
      console.error('Error loading customers:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load customers',
        timer: 2000
      });
    } finally {
      setLoading(false);
    }
  }, [canView, isSuperAdmin, pagination.page, pagination.limit, filters.search, sortOption, MySwal]);

  // Handle edit
  const handleEdit = (customer) => {
    if (!canEdit && !isSuperAdmin) {
      MySwal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to edit customers',
        icon: 'error',
        timer: 3000
      });
      return;
    }
    setSelectedCustomer(customer);
    setShowEditModal(true);
  };

  // Handle view
  const handleView = (customer) => {
    if (!canView && !isSuperAdmin) {
      MySwal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to view customer details',
        icon: 'error',
        timer: 3000
      });
      return;
    }
    setSelectedCustomer(customer);
    setShowViewModal(true);
  };

  // Handle delete
  const handleDelete = async (id, name) => {
    if (!canDelete && !isSuperAdmin) {
      MySwal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to delete customers',
        icon: 'error',
        timer: 3000
      });
      return;
    }

    const result = await MySwal.fire({
      title: 'Delete Customer?',
      html: `<p>Are you sure you want to delete <strong>${name}</strong>?</p>
             <p class="text-danger">This action cannot be undone.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const deleteResult = await customerActions.delete(id);

        if (deleteResult.success) {
          MySwal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Customer has been deleted.',
            timer: 2000
          });
          loadCustomers(pagination.page);
        } else {
          MySwal.fire({
            icon: 'error',
            title: 'Error',
            text: deleteResult.message || 'Failed to delete customer',
            timer: 2000
          });
        }
      } catch (error) {
        console.error('Error deleting customer:', error);
        MySwal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete customer',
          timer: 2000
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (!canDelete && !isSuperAdmin) {
      MySwal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to delete customers',
        icon: 'error',
        timer: 3000
      });
      return;
    }

    if (selectedRows.length === 0) {
      MySwal.fire({
        icon: 'info',
        title: 'No Selection',
        text: 'Please select at least one customer to delete.',
        timer: 2000
      });
      return;
    }

    const result = await MySwal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${selectedRows.length} customer(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: `Yes, delete ${selectedRows.length} item(s)`
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const deletePromises = selectedRows.map(id => customerActions.delete(id));
        const results = await Promise.allSettled(deletePromises);
        const successful = results.filter(r => r.status === 'fulfilled' && r.value.success);

        if (successful.length > 0) {
          MySwal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: `${successful.length} customer(s) deleted.`,
            timer: 2000
          });
          setSelectedRows([]);
          loadCustomers(pagination.page);
        } else {
          MySwal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to delete customers',
            timer: 2000
          });
        }
      } catch (error) {
        console.error('Error in bulk delete:', error);
        MySwal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete customers',
          timer: 2000
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle add customer
  const handleAddCustomer = () => {
    if (!canCreate && !isSuperAdmin) {
      MySwal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to create customers',
        icon: 'error',
        timer: 3000
      });
      return;
    }
    setShowAddModal(true);
  };

  // Handle success
  const handleSuccess = () => {
    loadCustomers(pagination.page);
    setShowAddModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setSelectedCustomer(null);
  };

  // Format date
  const formatDate = (date) => {
    return date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
  };

  // Get customer initials
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Get random color for avatar
  const getAvatarColor = (id) => {
    const colors = [
      'bg-primary',
      'bg-success',
      'bg-info',
      'bg-warning',
      'bg-danger',
      'bg-secondary'
    ];
    return colors[id % colors.length];
  };

  // Tooltip renderer
  const renderTooltip = (props, text) => (
    <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
  );

  // Initial load
  useEffect(() => {
    if (canView || isSuperAdmin) {
      loadCustomers();
    }
  }, []); // Empty dependency array

  // Update when sort, page, or limit changes
  useEffect(() => {
    if (canView || isSuperAdmin) {
      loadCustomers();
    }
  }, [sortOption, pagination.page, pagination.limit]);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="page-header">
          <div className="page-title">
            <h4>Customer Management</h4>
            <h6>Manage your customers and their details</h6>
          </div>

          <ul className="table-top-head">

            <li>
              <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                <Link onClick={() => loadCustomers()}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                <Link
                  className={data ? "active" : ""}
                  onClick={() => dispatch(setToogleHeader(!data))}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
            {/* Bulk Delete Button - Only show if user has delete permission */}
            {(canDelete || isSuperAdmin) && selectedRows.length > 0 && (
              <li>
                <button className="btn btn-danger btn-sm" onClick={handleBulkDelete}>
                  <Trash2 size={16} className="me-1" />
                  Delete ({selectedRows.length})
                </button>
              </li>
            )}
          </ul>

          {/* Add Customer Button - Only show if user has create permission */}
          <div className="page-btn">
            {(canCreate || isSuperAdmin) && (
              <button className="btn btn-primary" onClick={handleAddCustomer}>
                <PlusCircle size={18} className="me-2" />
                Add Customer
              </button>
            )}
          </div>
        </div>

        {/* Filters - Only show if user has view permission */}
        {(canView || isSuperAdmin) && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="row">
                <div className="col-lg-4">
                  <div className="mb-3">
                    <label>Search</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Name, email, phone..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        onKeyPress={(e) => e.key === 'Enter' && loadCustomers(1)}
                      />
                      <button className="btn btn-primary" onClick={() => loadCustomers(1)}>
                        <Search size={16} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="mb-3">
                    <label>Sort By</label>
                    <Select
                      options={sortOptions}
                      value={sortOptions.find(s => s.value === sortOption)}
                      onChange={(opt) => {
                        setSortOption(opt?.value || 'name_asc');
                        setPagination(prev => ({ ...prev, page: 1 }));
                      }}
                    />
                  </div>
                </div>
                <div className="col-lg-4 d-flex align-items-end">
                  <button className="btn btn-secondary" onClick={() => {
                    setFilters({ search: '', sortBy: 'name', sortOrder: 'ASC' });
                    setSortOption('name_asc');
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}>
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customers Grid/Table */}
        <div className="card">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : !canView && !isSuperAdmin ? (
              <div className="text-center py-5">
                <i data-feather="lock" className="feather-lock" style={{ fontSize: '48px', opacity: 0.5 }} />
                <h5 className="mt-3">Access Denied</h5>
                <p className="text-muted">You dont have permission to view customers</p>
              </div>
            ) : customers.length === 0 ? (
              <div className="text-center py-5">
                <Users size={48} className="text-muted mb-3" />
                <h5>No customers found</h5>
                <p className="text-muted">
                  {filters.search
                    ? "Try adjusting your search"
                    : "Add your first customer to get started"}
                </p>
                {(canCreate || isSuperAdmin) && (
                  <button className="btn btn-primary" onClick={handleAddCustomer}>
                    <PlusCircle size={16} className="me-2" />
                    Add Customer
                  </button>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      {/* Checkbox column - Only show if user has delete permission */}
                      {(canDelete || isSuperAdmin) && (
                        <th width="40px">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedRows.length === customers.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedRows(customers.map(c => c.id));
                              } else {
                                setSelectedRows([]);
                              }
                            }}
                          />
                        </th>
                      )}
                      <th>Customer</th>
                      <th>Contact</th>
                      <th>GST No</th>
                      <th>Total Sales</th>
                      <th>Total Amount</th>
                      <th>Due Amount</th>
                      <th>Last Sale</th>
                      <th>Joined</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr key={customer.id}>
                        {/* Checkbox cell - Only show if user has delete permission */}
                        {(canDelete || isSuperAdmin) && (
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedRows.includes(customer.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRows([...selectedRows, customer.id]);
                                } else {
                                  setSelectedRows(selectedRows.filter(id => id !== customer.id));
                                }
                              }}
                            />
                          </td>
                        )}
                        <td>
                          <div className="d-flex align-items-center">
                            <div className={`avatar avatar-sm me-2 ${getAvatarColor(customer.id)} rounded-circle d-flex align-items-center justify-content-center text-white`}>
                              {getInitials(customer.name)}
                            </div>
                            <div>
                              <strong>{customer.name}</strong>
                              {customer.email && (
                                <div className="small text-muted">{customer.email}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          {customer.phone ? (
                            <div>
                              <Phone size={12} className="me-1" />
                              {customer.phone}
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>{customer.gst_no || <span className="text-muted">-</span>}</td>
                        <td className="text-center">
                          <span className="badge bg-info">{customer.statistics?.total_sales || 0}</span>
                        </td>
                        <td className="fw-bold text-primary">
                          ₹{(customer.statistics?.total_amount || 0).toFixed(2)}
                        </td>
                        <td className={`fw-bold ${(customer.statistics?.total_due || 0) > 0 ? 'text-danger' : 'text-success'}`}>
                          ₹{(customer.statistics?.total_due || 0).toFixed(2)}
                        </td>
                        <td>
                          {customer.statistics?.last_sale_date
                            ? formatDate(customer.statistics.last_sale_date)
                            : <span className="text-muted">No sales</span>
                          }
                        </td>
                        <td>{formatDate(customer.created_at)}</td>
                        <td className="text-center">
                          {/* View Button - Show if user has view permission */}
                          {(canView || isSuperAdmin) && (
                            <button
                              className="btn btn-sm btn-outline-info me-1"
                              onClick={() => handleView(customer)}
                              title="View Details"
                            >
                              <Eye size={14} />
                            </button>
                          )}

                          {/* Edit Button - Show if user has edit permission */}
                          {(canEdit || isSuperAdmin) && (
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => handleEdit(customer)}
                              title="Edit"
                            >
                              <Edit size={14} />
                            </button>
                          )}

                          {/* Delete Button - Show if user has delete permission */}
                          {(canDelete || isSuperAdmin) && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(customer.id, customer.name)}
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && (canView || isSuperAdmin) && pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </div>
                <nav>
                  <ul className="pagination">
                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => loadCustomers(pagination.page - 1)}>
                        Previous
                      </button>
                    </li>
                    {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                      let pageNum = pagination.page;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      return (
                        <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => loadCustomers(pageNum)}>
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => loadCustomers(pagination.page + 1)}>
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddCustomer
          onSuccess={handleSuccess}
          onClose={() => setShowAddModal(false)}
          isOpen={showAddModal}
        />
      )}

      {showEditModal && selectedCustomer && (
        <EditCustomer
          customerId={selectedCustomer.id}
          customerData={selectedCustomer}
          onSuccess={handleSuccess}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showViewModal && selectedCustomer && (
        <ViewCustomer
          customerId={selectedCustomer.id}
          customerData={selectedCustomer}
          onClose={() => setShowViewModal(false)}
        />
      )}
    </div>
  );
};

export default CustomerList;